/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI
 * (C) Copyright IBM Corp. 2018, 2019
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../lib/@waca/core-client/js/core-client/ui/core/Class',
	'underscore'
], function(Class, _) {
	const MultilingualAttribute = Class.extend({
		translationLocale: null,

		init: function(originalName, locale, options) {
			MultilingualAttribute.inherited('init', this, arguments);

			if (typeof locale !== 'string') { //Locale is optional. If not set then the locale will be determined from the options.
				options = locale;
				locale = undefined;
			}

			this.defaultLocale = options.defaultLocale || 'Default';

			// Should always have content locale, default to en mostly for unit tests
			this.contentLocale = options.contentLocale || 'en';

			// If a new widget was added while in translation mode
			this.translationLocale = options.translationLocale;

			if (originalName && originalName.translationTable) {
				this.translationTable = originalName.translationTable;
			} else {
				this.translationTable = {};
				// If we're in translation mode that takes priority
				if (this.translationLocale) {
					this.setValue(originalName, this.translationLocale);
				} else if (locale) {
					this.setValue(originalName, locale);
				} else if (options.availableDashboardLocales) {
					var translationTable = [];
					// Build a dummy translationTable with all the locales available in the dashboard
					options.availableDashboardLocales.forEach((function (locale) {
						translationTable[locale] = 'dummyString';
					}));

					var bestLocale = this._determineCorrectLocale(translationTable);
					this.setValue(originalName, bestLocale);
				} else {
					this.setValue(originalName, this.defaultLocale);
				}
			}
		},

		/*
		** @params options {object} selected language, model, and the name of the attribute to be updated
		*
		 */
		startTranslation: function(selectedLanguage) {
			this.translationLocale = selectedLanguage;
		},

		stopTranslation: function() {
			this.translationLocale = null;
		},

		getCurrentModelLocale: function () {
			return this.translationLocale ? this.translationLocale : this._determineCorrectLocale(this.translationTable);
		},

		setValue: function (value, locale) {
			if (!locale) {
				console.error('Trying to set multilingual property without specifying a locale');
				return;
			}

			if (!value) {
				delete this.translationTable[locale];
			} else {
				if (typeof value === 'object' && value.translationTable) {
					this.translationTable[locale] = value.translationTable[locale];
				} else {
					this.translationTable[locale] = value;
				}
			}
		},

		getValue: function(locale) {
			if(locale) {
				return this.translationTable[locale];
			}

			if(this.translationLocale && this.translationTable[this.translationLocale] !== undefined) {
				return this.translationTable[this.translationLocale];
			}

			// Default to an empty string if no value is found.  Can happen for new models where there's no default for the property
			return this.translationTable[this._determineCorrectLocale(this.translationTable)] || '';
		},

		_determineCorrectLocale: function(translationTable) {
			//Display content locale
			if(translationTable[this.contentLocale] !== undefined) {
				return this.contentLocale;
			}

			//Language code is defined as the first two chars in a LCID string, eg. 'en' for 'en-ca'
			const langCode = this.contentLocale.substr(0,2);

			//If the default locale shares the language code of the content locale and exists, show that
			if(this.defaultLocale.indexOf(langCode) !== -1 && translationTable[this.defaultLocale] !== undefined) {
				return this.defaultLocale;
			}

			//Otherwise display language code entry, eg. 'nl-nl' would try for 'nl'
			if(translationTable[langCode] !== undefined) {
				return langCode;
			}

			//Otherwise display first existing language with same language code, eg. 'en-us' would try for 'en-ca'
			const similarLangCodes =
				Object.keys(translationTable)
					.filter(function(key) { return key.substring(0,2) === langCode; } )
					.sort(); //order not always guaranteed with Object.keys
			while(similarLangCodes.length > 0) {
				const relatedLangCode = similarLangCodes.shift();
				if(translationTable[relatedLangCode] !== undefined) {
					return relatedLangCode;
				}
			}

			//Otherwise display base locale
			if(translationTable[this.defaultLocale] !== undefined) {
				return this.defaultLocale;
			}

			//otherwise display the first defined alphabetical locale we find
			//this shouldn't be possible, but it's software!
			const keys = Object.keys(translationTable).sort(); //order not always guaranteed with Object.keys
			while(keys.length > 0) {
				const lastResortLocale = keys.shift();
				if(this.translationTable[lastResortLocale] !== undefined) {
					return lastResortLocale;
				}
			}

			//If somehow we don't find anything, return the default locale
			return this.defaultLocale || 'Default';
		},

		setDefaultLocale: function (selectedLanguage) {
			if(!this.translationTable.hasOwnProperty(selectedLanguage)) {
				this.switchLocale({ from: this.defaultLocale, to: selectedLanguage });
			}
			this.defaultLocale = selectedLanguage;
		},

		switchLocale: function (options) {
			const currentLocale = options.from;
			const newLocale = options.to;
			if(this.translationTable[currentLocale]){
				this.translationTable[newLocale] = this.translationTable[currentLocale];
				this.removeTranslation(currentLocale);
			}
		},

		removeTranslation: function (locale) {
			delete this.translationTable[locale];
		},

		toString: function() {
			return this.getValue();
		},

		toJSON: function() {
			return {
				translationTable: _.extend({}, this.translationTable)
			};
		},

		needsTranslation: function() {
			if(_.isEmpty(this.translationTable)) {
				return false;
			}
			return this.translationTable[this.translationLocale] === undefined;
		},

		/**
		 * Return an array of locales this MultilingualAttribute knows about
		 */
		getLanguages: function () {
			if (!this.translationTable) {
				return [];
			}

			return Object.keys(this.translationTable);
		}
	});

	return MultilingualAttribute;
});
